import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Typography,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  Box,
  FormHelperText,
  RadioGroup,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUpload from "../../GovernanceDashboard/DataEntry/FileUpload";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import styles from "../../../styles/social/social.module.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { get, put } from "../../../utils/api";
import { textError } from "../../../utils/validationMessage";
import {
  annualPolicyYesFieldErrors,
  getEffectiveDateMinMax,
  hasUploadedDocument,
  isAnnualPolicyUploadDisabled,
  shouldShowAnnualPolicyRevised,
} from "../../../utils/annualPolicyFormHelpers";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import SocialLayout from "../../../Layout/SocialLayout";
import WestIcon from "@mui/icons-material/West";
import DeleteModal from "../../../Components/Common/DeleteModal";

const SOCIAL_PRIMARY = "#1C486B";
const socialRadioSx = {
  color: SOCIAL_PRIMARY,
  "&.Mui-checked": { color: SOCIAL_PRIMARY },
};
const backButtonSx = {
  color: SOCIAL_PRIMARY,
  textTransform: "none",
  border: `1px solid ${SOCIAL_PRIMARY}`,
  "&:hover": { borderColor: SOCIAL_PRIMARY },
};
const primaryButtonSx = {
  textTransform: "none",
  backgroundColor: SOCIAL_PRIMARY,
  "&:hover": { backgroundColor: "#163a56" },
};

const removeCommas = (val) => {
  if (val === null || val === undefined) return "";
  return val.toString().replace(/,/g, "").trim();
};

const formatWithCommas = (val) => {
  const normalized = removeCommas(val);
  if (normalized === "") return "";
  if (!/^\d*\.?\d*$/.test(normalized)) return normalized;
  const [integerPart = "", decimalPart] = normalized.split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
};

const stripCommasFromNumericStrings = (input) => {
  if (Array.isArray(input)) return input.map(stripCommasFromNumericStrings);
  if (input && typeof input === "object") {
    return Object.fromEntries(
      Object.entries(input).map(([key, value]) => [
        key,
        stripCommasFromNumericStrings(value),
      ]),
    );
  }
  if (typeof input === "string" && /^\d{1,3}(,\d{3})*(\.\d+)?$/.test(input)) {
    return removeCommas(input);
  }
  return input;
};

const BooleanInputField = ({ label, name, value, onChange }) => (
  <FormControl
    component="fieldset"
    style={{ marginBottom: label === "Is the Policy revised?" ? 0 : "1rem" }}
  >
    <p
      className={styles.form_heading}
      style={{ fontWeight: "bold", marginTop: "1rem" }}
    >
      {label}
    </p>
    <RadioGroup
      row
      name={name}
      value={value === false || value === "false" ? "false" : "true"}
      onChange={onChange}
      sx={{ pl: 2 }}
    >
      <FormControlLabel
        value="true"
        control={<Radio sx={socialRadioSx} />}
        label="Yes"
      />
      <FormControlLabel
        value="false"
        control={<Radio sx={socialRadioSx} />}
        label="No"
      />
    </RadioGroup>
  </FormControl>
);

const NumericInputField = ({
  label,
  name,
  errors,
  value,
  onChange,
  placeholder,
}) => (
  <div style={{ marginBottom: "1rem" }}>
    <p
      className={styles.form_heading}
      style={{ fontWeight: "bold", marginTop: "1rem" }}
    >
      {label}
    </p>
    <TextField
      name={name}
      value={formatWithCommas(value)}
      onChange={onChange}
      variant="outlined"
      size="small"
      sx={{ pl: 2, width: "50%" }}
      placeholder={placeholder}
      error={errors?.[name]}
    />
    {errors?.[name] && (
      <FormHelperText
        sx={{ color: "#D34040", marginLeft: "1rem", fontSize: "14px" }}
      >
        {errors?.[name]}
      </FormHelperText>
    )}
  </div>
);

const HealthAndSafety = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [errors, setErrors] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteData, setDeleteData] = useState({ fieldName: "", index: "" });
  const [module, setModule] = useState("monthly");
  const [
    existingHealthAndSafetyPolicyDocUrl,
    setExistingHealthAndSafetyPolicyDocUrl,
  ] = useState("");
  const [prevHealthAndSafetyPolicy, setPrevHealthAndSafetyPolicy] = useState({
    documentUrl: "",
    remark: "",
    effectiveDate: "",
  });
  const [isHealthPolicyDocumentDeleted, setIsHealthPolicyDocumentDeleted] =
    useState(false);

  // Annual formData
  const annualFormDataInitial = {
    healthAndSafetyPolicy: {
      status: true,
      isRevised: false,
      documentUrl: "",
      remark: "",
      statusremark: "",
      effectiveDate: "",
    },
    healthAndSafetyCommittee: { status: true, documentUrl: "" },
    onSiteClinic: {
      exists: true,
      type: "",
    },
    fireManagementProcedure: { status: true, documentUrl: "" },
    alertSystem: { status: true, documentUrl: "" },
    ISO45001Certified: { status: true, documentUrl: "" },
    emergencyPreparednessPlan: { status: true, remark: "", documentUrl: "" },
  };

  // Monthly formData
  const monthlyFormDataInitial = {
    lastInspectionDate: { status: true, date: "", documentUrl: "" },
    equipmentCalibrationList: [
      { equipment: "", calibrationDate: "", customEquipment: "" },
    ],
    PPEProvided: { status: "Yes", remark: "", documentUrl: "" },
    incidentCount: "",
  };

  const [annualFormData, setAnnualFormData] = useState(annualFormDataInitial);
  const [isFirstEntry, setIsFirstEntry] = useState(false);
  const [previousYearHealthPolicyDoc, setPreviousYearHealthPolicyDoc] =
    useState("");
  const [entryYear, setEntryYear] = useState(null);
  const todayIso = new Date().toISOString().split("T")[0];
  const currentYear = new Date().getFullYear();
  const maxEffectiveDate =
    Number(entryYear) && Number(entryYear) < currentYear
      ? `${entryYear}-12-31`
      : todayIso;
  const { min: healthPolicyEffectiveMin, max: healthPolicyEffectiveMax } =
    getEffectiveDateMinMax(entryYear);
  const [monthlyFormData, setMonthlyFormData] = useState(
    monthlyFormDataInitial,
  );
  const [equipmentLists, setEquipmentList] = useState([]);

  // Get the appropriate formData based on module
  const formData = module === "annual" ? annualFormData : monthlyFormData;
  const setFormData =
    module === "annual" ? setAnnualFormData : setMonthlyFormData;

  const isCalibrationRowComplete = useCallback((row) => {
    if (!row) return false;
    const equipment = (row.equipment || "").trim();
    const calibrationDate = (row.calibrationDate || "").trim();
    const customEquipment = (row.customEquipment || "").trim();

    if (equipment === "Others") {
      return customEquipment !== "" && calibrationDate !== "";
    }
    return equipment !== "" && calibrationDate !== "";
  }, []);

  const isAddCalibrationDisabled = useMemo(() => {
    const list =
      formData.equipmentCalibrationList &&
      formData.equipmentCalibrationList.length > 0
        ? formData.equipmentCalibrationList
        : [{ equipment: "", calibrationDate: "", customEquipment: "" }];
    const lastRow = list[list.length - 1];
    return !isCalibrationRowComplete(lastRow);
  }, [formData.equipmentCalibrationList, isCalibrationRowComplete]);

  const deleteCalibrationRow = (index) => {
    setFormData((prev) => {
      const currentList = prev.equipmentCalibrationList || [
        {
          equipment: "",
          calibrationDate: "",
          customEquipment: "",
        },
      ];
      const updatedList = currentList.filter((_, i) => i !== index);
      const finalList =
        updatedList.length > 0
          ? updatedList
          : [
              {
                equipment: "",
                calibrationDate: "",
                customEquipment: "",
              },
            ];
      return {
        ...prev,
        equipmentCalibrationList: finalList,
      };
    });
    setErrors((prev) => {
      const updatedErrors = { ...prev };
      if (updatedErrors.equipmentCalibrationList) {
        const updatedArrayErrors = updatedErrors.equipmentCalibrationList.filter(
          (_, i) => i !== index,
        );
        if (updatedArrayErrors.length === 0) {
          delete updatedErrors.equipmentCalibrationList;
        } else {
          updatedErrors.equipmentCalibrationList = updatedArrayErrors;
        }
      }
      return updatedErrors;
    });
  };

  const handleDelete = () => {
    if (deleteData.fieldName === "equipmentCalibrationList") {
      deleteCalibrationRow(Number(deleteData.index));
    }
    setDeleteDialog(false);
    setDeleteData({ fieldName: "", index: "" });
  };

  const validation = () => {
    let newErrors = {};
    let hasErrors = false;

    const isEmpty = (value) =>
      value === "" || value === null || value === undefined;
    const hasDocumentValue = (value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string") return value.trim() !== "";
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "object") return Object.keys(value).length > 0;
      return Boolean(value);
    };

    const validateRecursive = (data, errorsObj) => {
      Object.entries(data).forEach(([key, value]) => {
        // Skip customEquipment validation - it's handled separately in equipmentCalibrationList
        if (key === "customEquipment") {
          return; // Skip validation for customEquipment - it's handled in equipmentCalibrationList section
        }
        if (Array.isArray(value)) {
          // Special handling for equipmentCalibrationList
          if (key === "equipmentCalibrationList") {
            value.forEach((item, index) => {
              if (!errorsObj[key]) {
                errorsObj[key] = [];
              }
              if (!errorsObj[key][index]) {
                errorsObj[key][index] = {};
              }

              // Check if equipment is selected
              if (isEmpty(item.equipment)) {
                errorsObj[key][index].equipment = textError;
                hasErrors = true;
              } else if (item.equipment === "Others") {
                // Only validate customEquipment if "Others" is selected
                if (isEmpty(item.customEquipment)) {
                  errorsObj[key][index].customEquipment = textError;
                  hasErrors = true;
                }
              }

              if (isEmpty(item.calibrationDate)) {
                errorsObj[key][index].calibrationDate = textError;
                hasErrors = true;
              }
            });
          } else {
            // For other arrays, use the default recursive validation
            let arrayErrors = value
              .map((item, index) => {
                if (typeof item === "object") {
                  let nestedErrors = {};
                  validateRecursive(item, nestedErrors);
                  return Object.keys(nestedErrors).length > 0
                    ? nestedErrors
                    : null;
                } else {
                  return isEmpty(item) ? textError : null;
                }
              })
              .filter((error) => error !== null);

            if (arrayErrors.length > 0) {
              errorsObj[key] = arrayErrors;
              hasErrors = true;
            }
          }
        } else if (typeof value === "object" && value !== null) {
          console.log(data, "value+++");
          let nestedErrors = {};
          if (Object.keys(data).includes("onSiteClinic")) {
            console.log(value, "foronsite");
            if (
              value.exists === true &&
              (value.type === "" ||
                value.type === false ||
                value.type === "false")
            ) {
              hasErrors = true;
              errorsObj[key] = textError;
            }
          }
          if (Object.keys(data).includes("ISO45001Certified")) {
            console.log(value, "foronsite");
            if (value.status === true && value.documentUrl === "") {
              hasErrors = true;
              errorsObj[key] = textError;
            }
          }
          if (key === "healthAndSafetyCommittee") {
            console.log(value, "foronsite");
            if (value.status === true && value.documentUrl === "") {
              hasErrors = true;
              errorsObj[key] = textError;
            }
          }
          if (Object.keys(data).includes("fireSafetyEquipmentInspections")) {
            console.log(value, "foronsite");
            if (value.status === true && value.documentUrl === "") {
              hasErrors = true;
              errorsObj[key] = textError;
            }
          }
          if (Object.keys(data).includes("fireManagementProcedure")) {
            console.log(value, "foronsite");
            if (value.status === true && value.documentUrl === "") {
              hasErrors = true;
              errorsObj[key] = textError;
            }
          }
          if (Object.keys(data).includes("safetyMockDrills")) {
            console.log(value, "foronsite");
            if (value.status === true && value.documentUrl === "") {
              hasErrors = true;
              errorsObj[key] = textError;
            }
          }
          if (Object.keys(data).includes("alertSystem")) {
            console.log(value, "foronsite");
            if (value.status === true && value.documentUrl === "") {
              hasErrors = true;
              errorsObj[key] = textError;
            }
          }
          if (Object.keys(data).includes("emergencyPreparednessPlan")) {
            console.log(value, "foronsite");
            if (
              (value.status === true || value.status === "true") &&
              value.documentUrl === ""
            ) {
              hasErrors = true;
              errorsObj[key] = textError;
            }
          }
          if (key === "PPEProvided") {
            console.log(value, "for PPEProvided");
            if (
              value.status === undefined ||
              value.status === null ||
              value.status === ""
            ) {
              hasErrors = true;
              errorsObj[key] = {
                ...(errorsObj[key] || {}),
                status: textError, // e.g., "Status is required"
              };
            } else if (module === "monthly") {
              if (
                value.status === "Yes" ||
                value.status === true ||
                value.status === "true"
              ) {
                // Yes => documentUrl mandatory (remark optional)
                if (!value.documentUrl || value.documentUrl === "") {
                  hasErrors = true;
                  errorsObj[key] = {
                    ...(errorsObj[key] || {}),
                    documentUrl: textError,
                  };
                }
              } else if (
                value.status === "No" ||
                value.status === false ||
                value.status === "false"
              ) {
                // No => remark mandatory, document not required
                if (!value.remark || value.remark.trim() === "") {
                  hasErrors = true;
                  errorsObj[key] = {
                    ...(errorsObj[key] || {}),
                    remark: textError,
                  };
                }
              } else if (value.status === "NA") {
                // NA => nothing required
              }
            }
          }
          if (key === "lastInspectionDate") {
            if (value.status === true) {
              if (isEmpty(value.date)) {
                hasErrors = true;
                errorsObj[key] = {
                  ...(errorsObj[key] || {}),
                  date: textError,
                };
              }
              if (!hasDocumentValue(value.documentUrl)) {
                hasErrors = true;
                errorsObj[key] = {
                  ...(errorsObj[key] || {}),
                  documentUrl: textError,
                };
              }
            }
          }

          if (Object.keys(nestedErrors).length > 0) {
            errorsObj[key] = nestedErrors;
            hasErrors = true;
          }
        } else if (isEmpty(value)) {
          errorsObj[key] = textError;
          hasErrors = true;
        }
      });
    };

    // Only validate fields relevant to the current module
    if (module === "annual") {
      validateRecursive(annualFormData, newErrors);
      const pol = annualFormData.healthAndSafetyPolicy;
      if (pol?.status) {
        const showRev = shouldShowAnnualPolicyRevised(
          !isFirstEntry,
          previousYearHealthPolicyDoc,
        );
        const polErr = annualPolicyYesFieldErrors(pol, entryYear, showRev);
        if (Object.keys(polErr).length > 0) {
          newErrors.healthAndSafetyPolicy = {
            ...(typeof newErrors.healthAndSafetyPolicy === "object" &&
            newErrors.healthAndSafetyPolicy
              ? newErrors.healthAndSafetyPolicy
              : {}),
            ...polErr,
          };
          hasErrors = true;
        }
      }
    } else {
      validateRecursive(monthlyFormData, newErrors);
      if (isEmpty(monthlyFormData.incidentCount)) {
        newErrors.incidentCount = textError;
        hasErrors = true;
      }
    }
    setErrors(newErrors);
    return hasErrors;
  };

  const fetchData = useCallback(
    async (id) => {
      try {
        const response = await get(`/social/getSocial?id=${id}`);
        const responseData = response?.data?.data;
        const socialData = Array.isArray(responseData)
          ? responseData[0]
          : responseData;
        const fetchedData = socialData?.healthAndSafety || {};
        const sanitizeDocumentUrl = (value) => {
          if (typeof value !== "string") return value || "";
          const cleaned = value.trim();
          if (!cleaned) return "";
          if (cleaned.toLowerCase() === "no documents available") return "";
          return cleaned;
        };
        const currentEntryYear =
          Number(socialData?._socialDataRef?.yearToSubmit) ||
          Number(socialData?.yearToSubmit) ||
          Number(socialData?.year) ||
          undefined;
        setEntryYear(currentEntryYear || null);
        const companyId = socialData?.company;
        let prevHealthPolicy = {};
        try {
          const qs = new URLSearchParams();
          if (currentEntryYear) qs.set("year", String(currentEntryYear));
          if (companyId) qs.set("companyId", String(companyId));
          const prevUrl = `/social/totalWorkforce/annualRangeWithPrev${qs.toString() ? `?${qs.toString()}` : ""}`;
          const prevRes = await get(prevUrl);
          setIsFirstEntry(Boolean(prevRes?.data?.data?.isFirstEntry));
          const prevPolicies = prevRes?.data?.data?.policies || {};
          prevHealthPolicy = prevPolicies?.["healthAndSafety.healthAndSafetyPolicy"] || {};
          setPreviousYearHealthPolicyDoc(
            sanitizeDocumentUrl(prevHealthPolicy?.documentUrl || ""),
          );
        } catch (e) {
          prevHealthPolicy = {};
          setIsFirstEntry(false);
          setPreviousYearHealthPolicyDoc("");
        }

        // Get module from URL params, API response, or default to monthly
        const urlModule = searchParams.get("entryType");
        const apiModule =
          socialData?.module || socialData?._socialDataRef?.module;
        let finalModule = "monthly";

        if (
          urlModule === "annual" ||
          apiModule === "Annual" ||
          apiModule === "annual"
        ) {
          finalModule = "annual";
        } else if (
          urlModule === "monthly" ||
          apiModule === "Monthly" ||
          apiModule === "monthly"
        ) {
          finalModule = "monthly";
        }

        setModule(finalModule);

        // Separate annual and monthly data
        const annualData = {};
        const monthlyData = {};

        if (fetchedData) {
          // Annual fields
          const annualFields = [
            "healthAndSafetyPolicy",
            "healthAndSafetyCommittee",
            "onSiteClinic",
            "fireManagementProcedure",
            "alertSystem",
            "ISO45001Certified",
            "emergencyPreparednessPlan",
          ];

          // Monthly fields
          const monthlyFields = [
            "lastInspectionDate",
            "equipmentCalibrationList",
            "PPEProvided",
            "incidentCount",
          ];

          Object.keys(fetchedData).forEach((key) => {
            if (annualFields.includes(key)) {
              annualData[key] = fetchedData[key];
            } else if (monthlyFields.includes(key)) {
              monthlyData[key] =
                key === "incidentCount"
                  ? fetchedData[key] === 0 || fetchedData[key] === "0"
                    ? ""
                    : formatWithCommas(fetchedData[key])
                  : fetchedData[key];
            }
          });
        }

        // Default all boolean radios to "Yes" when API payload omits status/exists
        [
          "healthAndSafetyPolicy",
          "healthAndSafetyCommittee",
          "fireManagementProcedure",
          "alertSystem",
          "ISO45001Certified",
          "emergencyPreparednessPlan",
        ].forEach((key) => {
          if (annualData[key] && typeof annualData[key] === "object") {
            annualData[key] = {
              ...annualData[key],
              status:
                annualData[key].status === undefined
                  ? true
                  : annualData[key].status,
            };
          }
        });

        if (
          annualData.healthAndSafetyPolicy &&
          typeof annualData.healthAndSafetyPolicy === "object"
        ) {
          const prevDocUrl = sanitizeDocumentUrl(prevHealthPolicy?.documentUrl || "");
          annualData.healthAndSafetyPolicy = {
            ...annualData.healthAndSafetyPolicy,
            isRevised:
              annualData.healthAndSafetyPolicy.isRevised === undefined
                ? Boolean(prevHealthPolicy?.isRevised) || false
                : annualData.healthAndSafetyPolicy.isRevised,
            status:
              annualData.healthAndSafetyPolicy.status === undefined
                ? prevHealthPolicy?.status === undefined
                  ? true
                  : Boolean(prevHealthPolicy?.status)
                : annualData.healthAndSafetyPolicy.status,
            documentUrl:
              sanitizeDocumentUrl(
                annualData.healthAndSafetyPolicy.documentUrl || "",
              ) || prevDocUrl,
            remark:
              annualData.healthAndSafetyPolicy.remark ||
              prevHealthPolicy?.remark ||
              "",
            effectiveDate:
              annualData.healthAndSafetyPolicy.effectiveDate ||
              prevHealthPolicy?.effectiveDate ||
              "",
          };
        }

        const initialPrevHealthAndSafetyPolicyDocUrl =
          sanitizeDocumentUrl(annualData?.healthAndSafetyPolicy?.documentUrl || "") ||
          sanitizeDocumentUrl(prevHealthPolicy?.documentUrl || "");
        setExistingHealthAndSafetyPolicyDocUrl(
          initialPrevHealthAndSafetyPolicyDocUrl,
        );
        setPrevHealthAndSafetyPolicy({
          documentUrl: initialPrevHealthAndSafetyPolicyDocUrl,
          remark:
            annualData?.healthAndSafetyPolicy?.remark ||
            prevHealthPolicy?.remark ||
            "",
          effectiveDate:
            annualData?.healthAndSafetyPolicy?.effectiveDate ||
            prevHealthPolicy?.effectiveDate ||
            "",
        });

        if (
          annualData.onSiteClinic &&
          typeof annualData.onSiteClinic === "object"
        ) {
          annualData.onSiteClinic = {
            ...annualData.onSiteClinic,
            exists:
              annualData.onSiteClinic.exists === undefined
                ? true
                : annualData.onSiteClinic.exists,
          };
        }

        if (
          monthlyData.lastInspectionDate &&
          typeof monthlyData.lastInspectionDate === "object"
        ) {
          monthlyData.lastInspectionDate = {
            ...monthlyData.lastInspectionDate,
            status:
              monthlyData.lastInspectionDate.status === undefined
                ? true
                : monthlyData.lastInspectionDate.status,
          };
        }

        // Update annual form data
        setAnnualFormData((prevState) => ({
          ...prevState,
          ...annualData,
        }));

        // Update monthly form data
        setMonthlyFormData((prevState) => {
          const updated = {
            ...prevState,
            ...monthlyData,
          };
          // Ensure equipmentCalibrationList always has at least one row
          if (
            !updated.equipmentCalibrationList ||
            updated.equipmentCalibrationList.length === 0
          ) {
            updated.equipmentCalibrationList = [
              { equipment: "", calibrationDate: "", customEquipment: "" },
            ];
          }
          // Ensure PPEProvided status defaults to "Yes" if not present
          if (
            !updated.PPEProvided ||
            !updated.PPEProvided.status ||
            updated.PPEProvided.status === ""
          ) {
            updated.PPEProvided = {
              ...updated.PPEProvided,
              status: updated.PPEProvided?.status || "Yes",
            };
          }
          return updated;
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },
    [searchParams],
  );

  useEffect(() => {
    fetchData(id);
  }, [id, fetchData]);

  // Fetch equipment list from API
  useEffect(() => {
    const fetchEquipmentList = async () => {
      try {
        const response = await get(
          "/master/getAllIndustrialEquipmentWithoutPagination",
        );
        const equipmentData = response?.data?.data || [];
        console.log(equipmentData, "equipmentData");
        // Extract unique equipmentType names
        const namesSet = new Set();
        equipmentData.forEach((item) => {
          const name = item?.equipmentType?.name;
          if (name) {
            namesSet.add(name);
          }
        });
        setEquipmentList(Array.from(namesSet));
      } catch (error) {
        console.error("Error fetching equipment list:", error);
        setEquipmentList([]);
      }
    };
    fetchEquipmentList();
  }, []);

  const handleBooleanChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === "true",
    }));
  };
  const handlePolicyChange = (e, policy) => {
    const { value, name } = e.target;
    console.log(name, "for handling");
    const isStatusChange = name === "status";
    const isRevisedChange = name === "isRevised";
    const isHealthAndSafetyPolicy = policy === "healthAndSafetyPolicy";
    const revisedValue = value === "true";
    if (isHealthAndSafetyPolicy && isRevisedChange && !revisedValue) {
      setIsHealthPolicyDocumentDeleted(false);
    }
    setFormData((prev) => ({
      ...prev,
      [policy]: {
        ...prev[policy],
        [name]:
          name === "remark" || name === "statusremark" || name === "effectiveDate"
            ? value
            : value === "true"
              ? true
              : false,
        ...(isHealthAndSafetyPolicy && isRevisedChange && !revisedValue
          ? {
              documentUrl:
                existingHealthAndSafetyPolicyDocUrl ||
                prevHealthAndSafetyPolicy.documentUrl ||
                prev[policy]?.documentUrl ||
                "",
              remark:
                prevHealthAndSafetyPolicy.remark ||
                prev[policy]?.remark ||
                "",
              effectiveDate:
                prevHealthAndSafetyPolicy.effectiveDate ||
                prev[policy]?.effectiveDate ||
                "",
            }
          : {}),
        ...(isHealthAndSafetyPolicy && isRevisedChange && revisedValue
          ? { documentUrl: "", remark: "", effectiveDate: "" }
          : {}),
        ...(isHealthAndSafetyPolicy && isStatusChange && value === "false"
          ? { isRevised: false, documentUrl: "", effectiveDate: "" }
          : {}),
      },
    }));
    setErrors((prev) => {
      if (policy !== "healthAndSafetyPolicy") {
        return { ...prev, [policy]: "" };
      }
      const next = { ...prev };
      if (name === "status" || name === "isRevised") {
        delete next.healthAndSafetyPolicy;
        return next;
      }
      const cur =
        typeof next.healthAndSafetyPolicy === "object" &&
        next.healthAndSafetyPolicy
          ? { ...next.healthAndSafetyPolicy }
          : {};
      if (name === "remark" && String(value || "").trim()) delete cur.remark;
      if (name === "effectiveDate") delete cur.effectiveDate;
      if (Object.keys(cur).length) next.healthAndSafetyPolicy = cur;
      else delete next.healthAndSafetyPolicy;
      return next;
    });
  };
  const handleNestedBooleanChange = (e) => {
    const { name, value } = e.target;
    console.log(name, !!value, "code for the ", typeof value);
    const [parent, key] = name.split(".");
    // setFormData((prev) => ({
    //   ...prev,
    //   [parent]: {
    //     ...prev[parent],
    //     [key]: value === "true",
    //   },
    // }));

    setFormData((prev) => {
      let updatedParent = {
        ...prev[parent], // Spread existing nested object
        [key]: value === "true",
      };

      if ("type" in updatedParent && updatedParent.exists === false) {
        updatedParent.type = "false";
      } else if ("type" in updatedParent && updatedParent.exists === true) {
        if (updatedParent.type === false || updatedParent.type === "false") {
          updatedParent.type = "";
        }
      }
      if ("documentUrl" in updatedParent && updatedParent.status === false) {
        updatedParent.documentUrl = "";
      }

      return {
        ...prev,
        [parent]: updatedParent,
      };
    });
    setErrors((prev) => ({
      ...prev,
      [parent]: value !== false && "",
    }));
  };
  const handleNestChange = (e) => {
    const { name, value } = e.target;
    console.log(name, !!value, "code for the ", typeof value);
    const [parent, key] = name.split(".");
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [key]: value,
      },
    }));
    setErrors((prev) => ({
      ...prev,
      [parent]: value !== false && "",
    }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue = removeCommas(value);

    if (!/^\d*$/.test(normalizedValue)) return;

    setFormData((prev) => ({
      ...prev,
      [name]: formatWithCommas(normalizedValue),
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handlePercentageChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue = removeCommas(value);

    if (
      !/^\d*$/.test(normalizedValue) ||
      normalizedValue < 0 ||
      normalizedValue > 100
    )
      return;

    setFormData((prev) => ({
      ...prev,
      [name]: formatWithCommas(normalizedValue),
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleFileChange = (file, policy) => {
    if (policy === "healthAndSafetyPolicy") {
      setIsHealthPolicyDocumentDeleted(!hasUploadedDocument(file));
    }
    setFormData((prev) => ({
      ...prev,
      [policy]: {
        ...prev[policy],
        documentUrl: file,
      },
    }));
    if (policy === "healthAndSafetyPolicy" && hasUploadedDocument(file)) {
      setErrors((prev) => {
        const next = { ...prev };
        const cur =
          typeof next.healthAndSafetyPolicy === "object" &&
          next.healthAndSafetyPolicy
            ? { ...next.healthAndSafetyPolicy }
            : {};
        delete cur.documentUrl;
        if (Object.keys(cur).length) next.healthAndSafetyPolicy = cur;
        else delete next.healthAndSafetyPolicy;
        return next;
      });
    } else {
      setErrors((prev) => ({
        ...prev,
        ...(policy === "emergencyPreparednessPlan" ||
        policy === "ISO45001Certified" ||
        policy === "healthAndSafetyCommittee" ||
        policy === "fireManagementProcedure" ||
        policy === "alertSystem" ||
        policy === "safetyMockDrills" ||
        policy === "fireSafetyEquipmentInspections" ||
        policy === "lastInspectionDate" ||
        policy === "PPEProvided"
          ? { [policy]: "" }
          : {
              [policy]: {
                ...prev[policy],
                documentUrl: "",
              },
            }),
      }));
    }
  };

  const handleSubmit = async () => {
    if (validation()) return;

    // Send the appropriate formData based on module
    const dataToSubmit = module === "annual" ? annualFormData : monthlyFormData;

    // Clean up the payload: if status is false, set documentUrl to ""
    let cleanedFormData = {};
    if (module === "annual") {
      cleanedFormData = {
        ...dataToSubmit,
        healthAndSafetyPolicy: {
          ...dataToSubmit.healthAndSafetyPolicy,
          ...(dataToSubmit.healthAndSafetyPolicy.status === false
            ? { documentUrl: "", isRevised: false, remark: "", effectiveDate: "" }
            : {}),
        },
        healthAndSafetyCommittee: {
          ...dataToSubmit.healthAndSafetyCommittee,
          ...(dataToSubmit.healthAndSafetyCommittee.status === false
            ? { documentUrl: "" }
            : {}),
        },
      };
    } else {
      cleanedFormData = {
        ...dataToSubmit,
        lastInspectionDate: {
          ...dataToSubmit.lastInspectionDate,
          ...(dataToSubmit.lastInspectionDate.status === false
            ? { date: "", documentUrl: "" }
            : {}),
        },
      };
    }

    cleanedFormData = stripCommasFromNumericStrings(cleanedFormData);

    const payload = {
      healthAndSafety: cleanedFormData,
      stepsCompleted: {
        healthAndSafety: true,
      },
    };

    await put(`/social/updateSocial?Id=${id}`, payload)
      .then((res) => {
        const entryTypeParam = module === "annual" ? "annual" : "monthly";
        navigate(`/social/data/human-rights/${id}`);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const displayedHealthPolicyDocument = isHealthPolicyDocumentDeleted
    ? formData?.healthAndSafetyPolicy?.documentUrl || ""
    : formData?.healthAndSafetyPolicy?.documentUrl ||
      (formData?.healthAndSafetyPolicy?.isRevised
        ? ""
        : existingHealthAndSafetyPolicyDocUrl ||
          prevHealthAndSafetyPolicy.documentUrl ||
          "");

  return (
    <MainDashboardLayout sidebar={<SocialLayout />}>
      <DeleteModal
        onOpen={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
      />
      {/* <div style={{ padding: "1rem 0" }}>
        <span
          style={{
            fontSize: "14px",
            fontWeight: "400",
            color: "rgba(0, 0, 0, 0.8)",
          }}
        >
          Data{" "}
        </span>
        <span>
          <ChevronRightIcon />
        </span>{" "}
        <span
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "rgba(0, 0, 0, 1)",
          }}
        >
          Add New Entry
        </span>
      </div>
      <div
        style={{
          fontSize: "24px",
          fontWeight: "700",
          color: "#001632",
          marginBottom: "1rem",
        }}
      >
        Add New Entry
      </div> */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        <p className={styles.form_headingStrongUpper}>E. Health and Safety</p>

        {/* Annual Module */}
        {module === "annual" && (
          <>
            {/* 1. Workplace Health and Safety policy */}
            <BooleanInputField
              label="1. Workplace Health and Safety policy:"
              name="status"
              value={formData.healthAndSafetyPolicy.status ?? true}
              onChange={(e) => handlePolicyChange(e, "healthAndSafetyPolicy")}
            />
            <Box sx={{ mb: 0, mt: 0 }}>
              {formData.healthAndSafetyPolicy.status ? (
                <>
                  {shouldShowAnnualPolicyRevised(
                    !isFirstEntry,
                    previousYearHealthPolicyDoc,
                  ) && (
                    <BooleanInputField
                      label="Is the Policy revised?"
                      name="isRevised"
                      value={formData.healthAndSafetyPolicy.isRevised ?? false}
                      onChange={(e) =>
                        handlePolicyChange(e, "healthAndSafetyPolicy")
                      }
                    />
                  )}
                  <Box sx={{ ml: "-0.3rem" }}>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem", mb: 0.5, mt: 0 }}
                    >
                      Upload evidence (for Policy):
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        ml: "1.1rem",
                      }}
                    >
                      <Box
                        sx={{
                          width: hasUploadedDocument(displayedHealthPolicyDocument)
                            ? "233px"
                            : "100px",
                        }}
                      >
                        <FileUpload
                          setDocumentUrl={(file) =>
                            handleFileChange(file, "healthAndSafetyPolicy")
                          }
                          filesList={displayedHealthPolicyDocument}
                          isDisabled={isAnnualPolicyUploadDisabled(
                            !isFirstEntry,
                            previousYearHealthPolicyDoc,
                          )}
                        />
                        {typeof errors?.healthAndSafetyPolicy === "object" &&
                        errors.healthAndSafetyPolicy?.documentUrl ? (
                          <FormHelperText
                            sx={{
                              color: "#D34040",
                              fontSize: "14px",
                              minHeight: "20px",
                            }}
                          >
                            {errors.healthAndSafetyPolicy.documentUrl}
                          </FormHelperText>
                        ) : null}
                      </Box>
                      <Box
                        sx={{
                          width: "30%",
                          mt: hasUploadedDocument(displayedHealthPolicyDocument)
                            ? "15px"
                            : "10px",
                            ml:hasUploadedDocument(displayedHealthPolicyDocument) ? "0" : "1.1rem",
                        }}
                      >
                        <TextField
                          fullWidth
                          name="remark"
                          value={formData.healthAndSafetyPolicy.remark || ""}
                          onChange={(e) =>
                            handlePolicyChange(e, "healthAndSafetyPolicy")
                          }
                          placeholder="Enter Additional Text"
                          size="small"
                          multiline
                          minRows={1}
                          error={
                            !!(
                              typeof errors?.healthAndSafetyPolicy ===
                                "object" &&
                              errors.healthAndSafetyPolicy?.remark
                            )
                          }
                          helperText={
                            typeof errors?.healthAndSafetyPolicy === "object"
                              ? errors.healthAndSafetyPolicy?.remark || " "
                              : " "
                          }
                          InputProps={{ sx: { padding: "9px 12px" } }}
                        />
                      </Box>
                    </Box>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem",mb:"12px" }}
                    >
                      Effective date (for Policy):
                    </Typography>
                    <TextField
                      type="date"
                      name="effectiveDate"
                      value={formData.healthAndSafetyPolicy.effectiveDate || ""}
                      inputProps={{
                        min: "2021-01-01",
                        ...(healthPolicyEffectiveMax
                          ? { max: healthPolicyEffectiveMax }
                          : { max: maxEffectiveDate }),
                      }}
                      onChange={(e) =>
                        handlePolicyChange(e, "healthAndSafetyPolicy")
                      }
                      size="small"
                      error={
                        !!(
                          typeof errors?.healthAndSafetyPolicy === "object" &&
                          errors.healthAndSafetyPolicy?.effectiveDate
                        )
                      }
                      sx={{
                        width: "30%",
                        ml: "1.1rem",
                        mb: "0.25rem",
                        "& .MuiInputBase-root": { height: 40 },
                      }}
                    />
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        fontSize: "14px",
                        marginLeft: "1.1rem",
                        width: "30%",
                      }}
                    >
                      {typeof errors?.healthAndSafetyPolicy === "object"
                        ? errors.healthAndSafetyPolicy?.effectiveDate
                        : ""}
                    </FormHelperText>
                  </Box>
                </>
              ) : (
                <div
                  style={{
                    marginTop: "0.5rem",
                    marginBottom: "1rem",
                    marginLeft: "1rem",
                  }}
                >
                  {/* <p
                    className={styles.form_heading}
                    style={{ fontWeight: "bold" }}
                  >
                    Remark
                  </p> */}
                  <TextField
                    name="statusremark"
                    value={formData.healthAndSafetyPolicy.statusremark || ""}
                    onChange={(e) =>
                      handlePolicyChange(e, "healthAndSafetyPolicy")
                    }
                    placeholder="Enter Additional Text"
                    size="small"
                    sx={{ width: "40%" }}
                  />
                </div>
              )}
            </Box>

            {/* 2. Health and Safety Committee in place */}
            <BooleanInputField
              label="2. Health and Safety Committee in place:"
              name="status"
              value={formData.healthAndSafetyCommittee.status ?? true}
              onChange={(e) =>
                handlePolicyChange(e, "healthAndSafetyCommittee")
              }
            />
            <Box sx={{ mb: 0, mt: 0 }}>
              {formData.healthAndSafetyCommittee.status && (
                <>
                  <p
                    className={styles.form_heading}
                    style={{
                      fontWeight: "bold",
                      marginTop: "0rem",
                      marginLeft: "1rem",
                    }}
                  >
                    Upload evidence
                  </p>
                  <div style={{ marginTop: "0.5rem", marginLeft: "1rem" }}>
                    <FileUpload
                      setDocumentUrl={(file) =>
                        handleFileChange(file, "healthAndSafetyCommittee")
                      }
                      filesList={formData.healthAndSafetyCommittee.documentUrl}
                      isDisabled={true}
                    />
                  </div>
                </>
              )}
              {errors?.healthAndSafetyCommittee && (
                <FormHelperText
                  sx={{
                    color: "#D34040",
                    marginLeft: "1rem",
                    fontSize: "14px",
                  }}
                >
                  {errors?.healthAndSafetyCommittee}
                </FormHelperText>
              )}
            </Box>

            {/* 3. On-site Clinic */}
            <BooleanInputField
              label="3. On-site clinic:"
              name="onSiteClinic.exists"
              value={formData.onSiteClinic.exists}
              onChange={handleNestedBooleanChange}
            />
            {formData.onSiteClinic.exists === true && (
              <div style={{ marginBottom: "1rem",ml:"1.1rem" }}>
                <Typography variant="body1" sx={{ml:"15px"}}>
                  Provide Detail
                </Typography>
                <RadioGroup
                  row
                  name="onSiteClinic.type"
                  value={formData.onSiteClinic.type}
                  onChange={handleNestChange}
                  sx={{ pl: 2 }}
                >
                  <FormControlLabel
                    value="Permanent"
                    control={<Radio sx={socialRadioSx} />}
                    label="Permanent"
                  />
                  <FormControlLabel
                    value="On Call"
                    control={<Radio sx={socialRadioSx} />}
                    label="On Call"
                  />
                </RadioGroup>
                {errors?.["onSiteClinic"] && (
                  <FormHelperText
                    sx={{
                      color: "#D34040",
                      marginLeft: "1.1rem",
                      fontSize: "14px",
                    }}
                  >
                    {errors?.["onSiteClinic"]}
                  </FormHelperText>
                )}
              </div>
            )}

            {/* 4. Fire management procedure in place */}
            <BooleanInputField
              label="4. Fire management procedure in place:"
              name="status"
              value={formData.fireManagementProcedure.status ?? true}
              onChange={(e) => handlePolicyChange(e, "fireManagementProcedure")}
            />
            <Box sx={{ mb: 0, mt: 0 }}>
              {formData.fireManagementProcedure.status && (
                <>
                  <p
                    className={styles.form_heading}
                    style={{
                      fontWeight: "bold",
                      marginTop: "0rem",
                      marginLeft: "1rem",
                    }}
                  >
                    Upload evidence
                  </p>
                  <div style={{ marginTop: "0.5rem", marginLeft: "1rem" }}>
                    <FileUpload
                      setDocumentUrl={(file) =>
                        handleFileChange(file, "fireManagementProcedure")
                      }
                      filesList={formData.fireManagementProcedure.documentUrl}
                      isDisabled={true}
                    />
                  </div>
                </>
              )}
              {errors?.fireManagementProcedure && (
                <FormHelperText
                  sx={{
                    color: "#D34040",
                    marginLeft: "1rem",
                    fontSize: "14px",
                  }}
                >
                  {errors?.fireManagementProcedure}
                </FormHelperText>
              )}
            </Box>

            {/* 5. Alert / Alarm system in place */}
            <BooleanInputField
              label="5. Alert / Alarm system in place"
              name="status"
              value={formData.alertSystem.status ?? true}
              onChange={(e) => handlePolicyChange(e, "alertSystem")}
            />
            <Box sx={{ mb: 1, mt: 0 }}>
              {formData.alertSystem.status && (
                <>
                  <p
                    className={styles.form_heading}
                    style={{
                      fontWeight: "bold",
                      marginTop: "0rem",
                      marginLeft: "1rem",
                    }}
                  >
                    Upload evidence
                  </p>
                  <div style={{ marginTop: "0.5rem", marginLeft: "1rem" }}>
                    <FileUpload
                      setDocumentUrl={(file) =>
                        handleFileChange(file, "alertSystem")
                      }
                      filesList={formData.alertSystem.documentUrl}
                      isDisabled={true}
                    />
                  </div>
                </>
              )}
              {errors?.alertSystem && (
                <FormHelperText
                  sx={{
                    color: "#D34040",
                    marginLeft: "1rem",
                    fontSize: "14px",
                  }}
                >
                  {errors?.alertSystem}
                </FormHelperText>
              )}
            </Box>

            {/* 6. Are you ISO 45001 certified */}
            <BooleanInputField
              label="6. Are you ISO 45001 certified"
              name="status"
              value={formData.ISO45001Certified.status ?? true}
              onChange={(e) => handlePolicyChange(e, "ISO45001Certified")}
            />
            <Box sx={{ mb: 1, mt: 0 }}>
              {formData.ISO45001Certified.status && (
                <>
                  <p
                    className={styles.form_heading}
                    style={{
                      fontWeight: "bold",
                      marginTop: "0rem",
                      marginLeft: "1rem",
                    }}
                  >
                    Upload evidence
                  </p>
                  <div style={{ marginTop: "0.5rem", marginLeft: "1rem" }}>
                    <FileUpload
                      setDocumentUrl={(file) =>
                        handleFileChange(file, "ISO45001Certified")
                      }
                      filesList={formData.ISO45001Certified.documentUrl}
                      isDisabled={true}
                    />
                  </div>
                </>
              )}
              {errors?.ISO45001Certified && (
                <FormHelperText
                  sx={{
                    color: "#D34040",
                    marginLeft: "1rem",
                    fontSize: "14px",
                  }}
                >
                  {errors?.ISO45001Certified}
                </FormHelperText>
              )}
            </Box>

            {/* 7. Is the Emergency Preparedness plan in place */}
            <BooleanInputField
              label="7. Is the Emergency Preparedness plan in place"
              name="status"
              value={formData.emergencyPreparednessPlan.status ?? true}
              onChange={(e) =>
                handlePolicyChange(e, "emergencyPreparednessPlan")
              }
            />
            {formData.emergencyPreparednessPlan.status && (
              <>
                <div style={{ marginBottom: "1rem", marginTop: "0rem",marginLeft:"1.1rem" }}>
                  <p
                    className={styles.form_heading}
                    style={{ fontWeight: "bold", marginTop: "0rem" }}
                  >
                    Enter text for additional Details
                  </p>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "1rem",
                      alignItems: "flex-start",
                      mt: 0,
                      width: "70%",
                    }}
                  >
                     <FileUpload
                      setDocumentUrl={(file) =>
                        handleFileChange(file, "emergencyPreparednessPlan")
                      }
                      mt="6px"
                      filesList={formData.emergencyPreparednessPlan.documentUrl}
                      isDisabled={true}
                    />
                    <TextField
                      name="remark"
                      value={formData.emergencyPreparednessPlan?.remark || ""}
                      onChange={(e) =>
                        handlePolicyChange(e, "emergencyPreparednessPlan")
                      }
                      placeholder="Enter Additional Text"
                      size="small"
                      sx={{ marginTop: "10px", width: "40%" }}
                      multiline
                    />
                   
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                      }}
                    >
                    </Box>
                  </Box>
                  {errors?.emergencyPreparednessPlan && (
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        marginLeft: "0rem",
                        fontSize: "14px",
                        mt: 1,
                      }}
                    >
                      {/* emergencyPreparednessPlan errors can be an object; prefer documentUrl/remark strings */}
                      {errors.emergencyPreparednessPlan.documentUrl ||
                        errors.emergencyPreparednessPlan.remark ||
                        (typeof errors.emergencyPreparednessPlan === "string"
                          ? errors.emergencyPreparednessPlan
                          : "")}
                    </FormHelperText>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* Monthly Module */}
        {module === "monthly" && (
          <>
            {/* 1. Date of last inspection of safety equipments done */}
            <BooleanInputField
              label="1. Date of last inspection of fire and safety equipments done:"
              name="status"
              value={formData.lastInspectionDate.status ?? true}
              onChange={(e) => handlePolicyChange(e, "lastInspectionDate")}
            />
            {formData.lastInspectionDate.status && (
              <>
                <div style={{ marginBottom: "1rem", marginTop: "-1rem" }}>
                  <p
                    className={styles.form_heading}
                    style={{
                      fontWeight: "bold",
                      marginTop: "1rem",
                      marginLeft: "1rem",
                    }}
                  >
                    Select date
                  </p>
                  <TextField
                    type="date"
                    value={formData.lastInspectionDate.date || ""}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        lastInspectionDate: {
                          ...prev.lastInspectionDate,
                          date: e.target.value,
                        },
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        lastInspectionDate: {
                          ...(typeof prev.lastInspectionDate === "object"
                            ? prev.lastInspectionDate
                            : {}),
                          date: "",
                        },
                      }));
                    }}
                    variant="outlined"
                    size="small"
                    sx={{
                      pl: 2,
                      width: "50%",
                      mt: 1,
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#EDEDED",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid #E0E0E0",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid #E0E0E0",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid #1C486B",
                        },
                      },
                      "& input[type='date']": {
                        color: "#000",
                        "&::-webkit-calendar-picker-indicator": {
                          cursor: "pointer",
                        },
                      },
                    }}
                    InputLabelProps={{
                      shrink: false,
                    }}
                  />
                  {errors?.lastInspectionDate?.date && (
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        marginLeft: "1rem",
                        fontSize: "14px",
                      }}
                    >
                      {errors.lastInspectionDate.date}
                    </FormHelperText>
                  )}
                </div>
                <Box sx={{ mb: 1, mt: 0 }}>
                  <div style={{ marginTop: "-1.5rem", marginLeft: "1rem" }}>
                    <p
                      className={styles.form_heading}
                      style={{ fontWeight: "bold", marginTop: "1rem" }}
                    >
                      Upload evidence
                    </p>
                    <FileUpload
                      setDocumentUrl={(file) =>
                        handleFileChange(file, "lastInspectionDate")
                      }
                      filesList={formData.lastInspectionDate.documentUrl}
                      isDisabled={true}
                    />
                  </div>
                  {errors?.lastInspectionDate?.documentUrl && (
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        marginLeft: "1rem",
                        fontSize: "14px",
                      }}
                    >
                      {errors.lastInspectionDate.documentUrl}
                    </FormHelperText>
                  )}
                </Box>
              </>
            )}

            {/* 2. Last date of calibration of all equipment done */}
            <div style={{ marginBottom: "1rem", marginTop: "1rem" }}>
              <p
                className={styles.form_heading}
                style={{ fontWeight: "bold", marginTop: "1rem" }}
              >
                2. Last date of calibration of all equipment done?
              </p>

              <div style={{ position: "relative", marginTop: "10px" }}>
                <TableContainer
                  component={Paper}
                  sx={{ boxShadow: "none", maxWidth: "100%" }}
                >
                  <Table sx={{ width: "100%" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            backgroundColor: "#1C486B",
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "14px",
                            padding: "8px 16px",
                            borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                          }}
                        >
                          List of equipments
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: "#1C486B",
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "14px",
                            padding: "8px 16px",
                          }}
                        >
                          Select last calibration date
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const equipmentList =
                          formData.equipmentCalibrationList &&
                          formData.equipmentCalibrationList.length > 0
                            ? formData.equipmentCalibrationList
                            : [
                                {
                                  equipment: "",
                                  calibrationDate: "",
                                  customEquipment: "",
                                },
                              ];
                        return equipmentList.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell
                              sx={{
                                padding: "8px 16px",
                                borderRight: "1px solid #E0E0E0",
                              }}
                            >
                              {item.equipment === "Others" ? (
                                <TextField
                                  value={item.customEquipment || ""}
                                  onChange={(e) => {
                                    setFormData((prev) => {
                                      const updatedList = [
                                        ...(prev.equipmentCalibrationList ||
                                          []),
                                      ];
                                      updatedList[index] = {
                                        ...updatedList[index],
                                        customEquipment: e.target.value,
                                      };
                                      return {
                                        ...prev,
                                        equipmentCalibrationList: updatedList,
                                      };
                                    });
                                    setErrors((prev) => {
                                      const updatedErrors = { ...prev };
                                      if (
                                        updatedErrors
                                          .equipmentCalibrationList?.[index]
                                      ) {
                                        const updatedFieldErrors = {
                                          ...updatedErrors
                                            .equipmentCalibrationList[index],
                                        };
                                        delete updatedFieldErrors.customEquipment;
                                        if (
                                          Object.keys(updatedFieldErrors)
                                            .length === 0
                                        ) {
                                          const updatedArrayErrors = [
                                            ...(updatedErrors.equipmentCalibrationList ||
                                              []),
                                          ];
                                          updatedArrayErrors[index] = undefined;
                                          updatedErrors.equipmentCalibrationList =
                                            updatedArrayErrors.filter(
                                              (e) => e !== undefined,
                                            );
                                          if (
                                            updatedErrors
                                              .equipmentCalibrationList
                                              .length === 0
                                          ) {
                                            delete updatedErrors.equipmentCalibrationList;
                                          }
                                        } else {
                                          updatedErrors.equipmentCalibrationList =
                                            [
                                              ...(updatedErrors.equipmentCalibrationList ||
                                                []),
                                            ];
                                          updatedErrors.equipmentCalibrationList[
                                            index
                                          ] = updatedFieldErrors;
                                        }
                                      }
                                      return updatedErrors;
                                    });
                                  }}
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  placeholder="Enter equipment name"
                                  error={
                                    !!errors?.equipmentCalibrationList?.[index]
                                      ?.customEquipment
                                  }
                                  sx={{
                                    backgroundColor: "#FFFFFF",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      border: "none",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        border: "none",
                                      },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        border: "none",
                                      },
                                  }}
                                />
                              ) : (
                                <FormControl
                                  variant="outlined"
                                  size="small"
                                  sx={{ width: "100%" }}
                                >
                                  <Select
                                    value={item.equipment || ""}
                                    onChange={(e) => {
                                      setFormData((prev) => {
                                        const updatedList = [
                                          ...(prev.equipmentCalibrationList ||
                                            []),
                                        ];
                                        updatedList[index] = {
                                          ...updatedList[index],
                                          equipment: e.target.value,
                                          customEquipment:
                                            e.target.value === "Others"
                                              ? ""
                                              : "",
                                        };
                                        return {
                                          ...prev,
                                          equipmentCalibrationList: updatedList,
                                        };
                                      });
                                      setErrors((prev) => {
                                        const updatedErrors = { ...prev };
                                        if (
                                          updatedErrors
                                            .equipmentCalibrationList?.[index]
                                        ) {
                                          const updatedFieldErrors = {
                                            ...updatedErrors
                                              .equipmentCalibrationList[index],
                                          };
                                          delete updatedFieldErrors.equipment;
                                          if (
                                            Object.keys(updatedFieldErrors)
                                              .length === 0
                                          ) {
                                            const updatedArrayErrors = [
                                              ...(updatedErrors.equipmentCalibrationList ||
                                                []),
                                            ];
                                            updatedArrayErrors[index] =
                                              undefined;
                                            updatedErrors.equipmentCalibrationList =
                                              updatedArrayErrors.filter(
                                                (e) => e !== undefined,
                                              );
                                            if (
                                              updatedErrors
                                                .equipmentCalibrationList
                                                .length === 0
                                            ) {
                                              delete updatedErrors.equipmentCalibrationList;
                                            }
                                          } else {
                                            updatedErrors.equipmentCalibrationList =
                                              [
                                                ...(updatedErrors.equipmentCalibrationList ||
                                                  []),
                                              ];
                                            updatedErrors.equipmentCalibrationList[
                                              index
                                            ] = updatedFieldErrors;
                                          }
                                        }
                                        return updatedErrors;
                                      });
                                    }}
                                    displayEmpty
                                    sx={{
                                      backgroundColor: "#FFFFFF",
                                      border: "none",
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        border: "none",
                                      },
                                      "&:hover .MuiOutlinedInput-notchedOutline":
                                        {
                                          border: "none",
                                        },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                          border: "none",
                                        },
                                    }}
                                  >
                                    <MenuItem value="" disabled>
                                      Select equipment
                                    </MenuItem>
                                    {equipmentLists.map((name) => (
                                      <MenuItem key={name} value={name}>
                                        {name}
                                      </MenuItem>
                                    ))}
                                    <MenuItem value="Others">Others</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                              {errors?.equipmentCalibrationList?.[index]
                                ?.equipment && (
                                <FormHelperText
                                  sx={{
                                    color: "#D34040",
                                    fontSize: "12px",
                                    marginTop: "4px",
                                    marginLeft: "10px",
                                  }}
                                >
                                  {
                                    errors.equipmentCalibrationList[index]
                                      .equipment
                                  }
                                </FormHelperText>
                              )}
                              {errors?.equipmentCalibrationList?.[index]
                                ?.customEquipment && (
                                <FormHelperText
                                  sx={{
                                    color: "#D34040",
                                    fontSize: "12px",
                                    marginTop: "4px",
                                    marginLeft: "10px",
                                  }}
                                >
                                  {
                                    errors.equipmentCalibrationList[index]
                                      .customEquipment
                                  }
                                </FormHelperText>
                              )}
                            </TableCell>
                            <TableCell sx={{ padding: "8px 16px" }}>
                              <TextField
                                type="date"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={item.calibrationDate || ""}
                                onChange={(e) => {
                                  setFormData((prev) => {
                                    const updatedList = [
                                      ...(prev.equipmentCalibrationList || []),
                                    ];
                                    updatedList[index] = {
                                      ...updatedList[index],
                                      calibrationDate: e.target.value,
                                    };
                                    return {
                                      ...prev,
                                      equipmentCalibrationList: updatedList,
                                    };
                                  });
                                  setErrors((prev) => {
                                    const updatedErrors = { ...prev };
                                    if (
                                      updatedErrors.equipmentCalibrationList?.[
                                        index
                                      ]
                                    ) {
                                      const updatedFieldErrors = {
                                        ...updatedErrors
                                          .equipmentCalibrationList[index],
                                      };
                                      delete updatedFieldErrors.calibrationDate;
                                      if (
                                        Object.keys(updatedFieldErrors)
                                          .length === 0
                                      ) {
                                        const updatedArrayErrors = [
                                          ...(updatedErrors.equipmentCalibrationList ||
                                            []),
                                        ];
                                        updatedArrayErrors[index] = undefined;
                                        updatedErrors.equipmentCalibrationList =
                                          updatedArrayErrors.filter(
                                            (error) => error !== undefined,
                                          );
                                        if (
                                          updatedErrors.equipmentCalibrationList
                                            .length === 0
                                        ) {
                                          delete updatedErrors.equipmentCalibrationList;
                                        }
                                      } else {
                                        updatedErrors.equipmentCalibrationList =
                                          [
                                            ...(updatedErrors.equipmentCalibrationList ||
                                              []),
                                          ];
                                        updatedErrors.equipmentCalibrationList[
                                          index
                                        ] = updatedFieldErrors;
                                      }
                                    }
                                    return updatedErrors;
                                  });
                                }}
                                InputLabelProps={{ shrink: true }}
                                placeholder="Select date of selected equipment"
                                sx={{
                                  backgroundColor: "#FFFFFF",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      border: "none",
                                    },
                                }}
                              />
                              {errors?.equipmentCalibrationList?.[index]
                                ?.calibrationDate && (
                                <FormHelperText
                                  sx={{
                                    color: "#D34040",
                                    fontSize: "12px",
                                    marginTop: "4px",
                                    marginLeft: "10px",
                                  }}
                                >
                                  {
                                    errors.equipmentCalibrationList[index]
                                      .calibrationDate
                                  }
                                </FormHelperText>
                              )}
                            </TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
                {/* Delete icons positioned to the right of each row */}
                <div
                  style={{
                    position: "absolute",
                    right: "-30px",
                    top: "43px", // Height of header row
                    display: "flex",
                    flexDirection: "column",
                    gap: "0px",
                  }}
                >
                  {(() => {
                    const equipmentList =
                      formData.equipmentCalibrationList &&
                      formData.equipmentCalibrationList.length > 0
                        ? formData.equipmentCalibrationList
                        : [
                            {
                              equipment: "",
                              calibrationDate: "",
                              customEquipment: "",
                            },
                          ];
                    return equipmentList.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "53px", // Approximate row height
                          width: "40px",
                        }}
                      >
                        {equipmentList.length > 1 ? (
                          <IconButton
                            onClick={() => {
                              setDeleteDialog(true);
                              setDeleteData({
                                fieldName: "equipmentCalibrationList",
                                index,
                              });
                            }}
                            sx={{
                              color: "#D34040",
                              padding: "4px",
                              "&:hover": {
                                backgroundColor: "#FFEBEE",
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: "20px" }} />
                          </IconButton>
                        ) : null}
                      </div>
                    ));
                  })()}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "10px",
                }}
              >
                <Button
                  variant="outlined"
                  disabled={isAddCalibrationDisabled}
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      equipmentCalibrationList: [
                        ...(prev.equipmentCalibrationList || []),
                        {
                          equipment: "",
                          calibrationDate: "",
                          customEquipment: "",
                        },
                      ],
                    }));
                  }}
                  sx={{
                    textTransform: "none",
                    minWidth: "auto",
                    padding: "6px 12px",
                    whiteSpace: "nowrap",
                    backgroundColor: "#FFFFFF",
                    borderColor: "#1C486B",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    color: "#1C486B",
                    opacity: isAddCalibrationDisabled ? 0.5 : 1,
                    "&:hover": {
                      backgroundColor: "#FFFFFF",
                      borderColor: "#1C486B",
                    },
                  }}
                >
                  Add +
                </Button>
              </div>
            </div>

            {/* 3. Incident Count */}
            <NumericInputField
              label="3. Incident Count:"
              name="incidentCount"
              value={formData.incidentCount}
              onChange={handleNumberChange}
              placeholder="Enter Number"
              errors={errors}    
            />

            {/* 3. Provision of PPE's to workers/employees */}
            <div
              style={{
                // marginBottom: errors?.["PPEProvided"]?.status ? 0 : "1rem",
              }}
            >
              <p
                className={styles.form_heading}
                style={{ fontWeight: "bold", marginTop: "1rem" }}
              >
                4. Provision of PPE's to workers/employees:
              </p>
              <RadioGroup
                row
                name="status"
                value={
                  formData.PPEProvided?.status === true ||
                  formData.PPEProvided?.status === "true"
                    ? "Yes"
                    : formData.PPEProvided?.status === false ||
                        formData.PPEProvided?.status === "false"
                      ? "No"
                      : formData.PPEProvided?.status || "Yes"
                }
                onChange={(e) => {
                  const { name, value } = e.target;
                  setFormData((prev) => {
                    const updated = {
                      ...prev,
                      PPEProvided: {
                        ...prev.PPEProvided,
                        [name]: value,
                      },
                    };

                    if (name === "status") {
                      // Yes: show remark + upload (both mandatory)
                      // No: show remark only (mandatory)
                      // NA: show nothing
                      if (value === "NA") {
                        updated.PPEProvided.remark = "";
                        updated.PPEProvided.documentUrl = "";
                      } else if (value === "No") {
                        updated.PPEProvided.documentUrl = "";
                      }
                    }

                    return updated;
                  });
                  setErrors((prev) => ({
                    ...prev,
                    PPEProvided: {
                      ...prev?.PPEProvided,
                      [name]: "",
                      ...(name === "status" && value === "NA"
                        ? { remark: "", documentUrl: "" }
                        : name === "status" && value === "No"
                          ? { documentUrl: "" }
                          : {}),
                    },
                  }));
                }}
                sx={{ pl: 2 }}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio sx={socialRadioSx} />}
                  label="Yes"
                />
                <FormControlLabel
                  value="No"
                  control={<Radio sx={socialRadioSx} />}
                  label="No"
                />
                <FormControlLabel
                  value="NA"
                  control={<Radio sx={socialRadioSx} />}
                  label="NA"
                />
              </RadioGroup>
            </div>
            {errors?.PPEProvided?.status && (
              <FormHelperText
                sx={{ color: "#D34040", marginLeft: "1rem", fontSize: "14px" }}
              >
                {errors.PPEProvided?.status}
              </FormHelperText>
            )}
            {(formData?.PPEProvided?.status === "Yes" ||
              formData?.PPEProvided?.status === "No" ||
              formData?.PPEProvided?.status === true ||
              formData?.PPEProvided?.status === false ||
              formData?.PPEProvided?.status === "true" ||
              formData?.PPEProvided?.status === "false") && (
              <div style={{ marginBottom: "1rem", marginTop: "1rem",marginLeft:"1rem" }}>
                <p
                  className={styles.form_heading}
                  style={{ fontWeight: "bold",
                    //  marginTop: "1rem"
                     }}
                >
                  Additional Details
                </p>
                <TextField
                  name="remark"
                  value={formData.PPEProvided?.remark || ""}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    setFormData((prev) => ({
                      ...prev,
                      ["PPEProvided"]: {
                        ...prev?.["PPEProvided"],
                        [name]: value,
                      },
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      PPEProvided: {
                        ...prev?.PPEProvided,
                        [name]: "",
                      },
                    }));
                  }}
                  placeholder="Enter Additional Text"
                  errors={errors?.PPEProvided?.remark}
                  size="small"
                  sx={{ width: "50%" }}
                  multiline
                />
                {(formData?.PPEProvided?.status === "No" ||
                  formData?.PPEProvided?.status === false ||
                  formData?.PPEProvided?.status === "false") &&
                  errors?.["PPEProvided"]?.remark && (
                  <FormHelperText
                    sx={{
                      color: "#D34040",
                      marginLeft: "0.1rem",
                      fontSize: "14px",
                    }}
                  >
                    {errors?.["PPEProvided"]?.remark}
                  </FormHelperText>
                )}
              </div>
            )}

            {(formData?.PPEProvided?.status === "Yes" ||
              formData?.PPEProvided?.status === true ||
              formData?.PPEProvided?.status === "true") && (
              <Box sx={{ mb: 1, mt: 0 ,ml:"1rem"}}>
                <p
                  className={styles.form_heading}
                  style={{ fontWeight: "bold", marginTop: "0rem" }}
                >
                  Upload evidence
                </p>
                <div style={{ marginTop: "0.5rem" }}>
                  <FileUpload
                    setDocumentUrl={(file) =>
                      handleFileChange(file, "PPEProvided")
                    }
                    filesList={formData.PPEProvided.documentUrl}
                    isDisabled={true}
                  />
                </div>
                {errors?.PPEProvided?.documentUrl && (
                  <FormHelperText
                    sx={{
                      color: "#D34040",
                      marginLeft: "0.1rem",
                      fontSize: "14px",
                    }}
                  >
                    {errors?.PPEProvided?.documentUrl}
                  </FormHelperText>
                )}
              </Box>
            )}
          </>
        )}

        {/* Submit Button */}
        <Box
          sx={{
            width: "100%",
            mt: "10%",
            mb: 5,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            sx={backButtonSx}
            onClick={() => navigate(`/social/data/labour-practices/${id}`)}
          >
            <WestIcon sx={{ mr: 2 }} /> Back
          </Button>
          <Button
            variant="contained"
            className={styles.submit_button}
            onClick={handleSubmit}
            sx={primaryButtonSx}
          >
            Save & Continue <ArrowForwardIcon sx={{ ml: 1 }} />
          </Button>
        </Box>
      </div>
    </MainDashboardLayout>
  );
};

export default HealthAndSafety;
